import { motion } from "framer-motion";
import { MapPin, Building2, Landmark, Globe2 } from "lucide-react";
import { Reveal } from "../Reveal";
import { IMAGES, ADDRESS, EXTERNAL } from "../../lib/site";

const AREAS = [
  { icon: Landmark, title: "Washington, DC", desc: "Steps from the passport agency, embassies, and federal offices downtown." },
  { icon: MapPin, title: "Maryland & Virginia", desc: "The full DMV — Bethesda, Silver Spring, Arlington, Alexandria, Fairfax & more." },
  { icon: Building2, title: "PA, DE & the East Coast", desc: "Pennsylvania, Delaware, New Jersey, New York and the wider Eastern Seaboard." },
  { icon: Globe2, title: "Nationwide & Embassies", desc: "Remote clients across all 50 states plus hand-carried embassy visa filings." },
];

export const LocationSEO = () => (
  <section id="location" className="bg-[#f8f9fa] py-24 md:py-32" data-testid="location-seo">
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_30px_rgba(11,25,44,0.10)]">
            <img src={IMAGES.office} alt="UPV Services office — Davis Building, 1629 K St NW, Washington DC" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b192c]/85 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-xs uppercase tracking-widest text-white/80">Our office · Davis Building</p>
              <p className="font-display font-bold text-2xl mt-1">{ADDRESS}</p>
              <a href={EXTERNAL.google} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white" data-testid="location-map-link">
                <MapPin className="w-4 h-4" /> View on Google Maps
              </a>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">Serving the East Coast &amp; nationwide</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0b192c] tracking-tight">
              Local expertise where the embassies are.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
              Based in downtown Washington DC, we serve travelers up and down the East Coast — DC, Maryland, Virginia, Pennsylvania, Delaware and beyond — and expedite passports and visas for clients across the entire United States. Our location lets us hand-carry documents directly to the passport agency and foreign embassies, cutting days off standard timelines.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <motion.div
                  key={a.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white rounded-xl p-6 border border-black/5"
                  data-testid={`area-${i}`}
                >
                  <Icon className="w-6 h-6 text-[#d90429]" />
                  <h3 className="mt-4 font-semibold text-lg text-[#0b192c]">{a.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </section>
);
